import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { SESSION_EXPIRED } from '@/services/api/baseApi';

import { authApi } from '../services/authApi';
import type { AuthStatus, User, UserRole } from '../types/auth.types';

/**
 * Client-side view of the session.
 *
 * SECURITY: this slice is a UX convenience, not an authorization mechanism.
 * It records who the backend *said* is signed in so the UI can render the
 * right chrome and avoid flashing pages the user cannot use. Every value here
 * is trivially editable from browser devtools, so the backend remains the sole
 * authority on what any request is actually permitted to do.
 *
 * No credential is stored: the session lives in an HttpOnly cookie the client
 * cannot read.
 */

export interface AuthState {
  user: User | null;
  status: AuthStatus;
}

const initialState: AuthState = {
  user: null,
  status: 'idle',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Clears the session locally, e.g. on explicit sign-out. */
    sessionCleared(state) {
      state.user = null;
      state.status = 'unauthenticated';
    },
    sessionChecking(state) {
      state.status = 'checking';
    },
    sessionEstablished(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.status = 'authenticated';
    },
  },
  extraReducers: (builder) => {
    builder
      // The transport layer emits this when any request returns 401, so a
      // session that expires mid-use is reflected everywhere at once.
      .addCase(SESSION_EXPIRED, (state) => {
        state.user = null;
        state.status = 'unauthenticated';
      })
      .addMatcher(authApi.endpoints.getSession.matchPending, (state) => {
        // Only the first probe shows the blocking "checking" state; later
        // refetches must not blank out an established session.
        if (state.status === 'idle') {
          state.status = 'checking';
        }
      })
      .addMatcher(authApi.endpoints.getSession.matchFulfilled, (state, action) => {
        state.user = action.payload.user;
        state.status = 'authenticated';
      })
      .addMatcher(authApi.endpoints.getSession.matchRejected, (state) => {
        state.user = null;
        state.status = 'unauthenticated';
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        state.user = action.payload.user;
        state.status = 'authenticated';
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.user = null;
        state.status = 'unauthenticated';
      });
  },
  selectors: {
    selectCurrentUser: (state) => state.user,
    selectAuthStatus: (state) => state.status,
    selectIsAuthenticated: (state) => state.status === 'authenticated',
    /** True until the initial session probe settles; gates route rendering. */
    selectIsSessionResolved: (state) =>
      state.status === 'authenticated' || state.status === 'unauthenticated',
  },
});

export const { sessionCleared, sessionChecking, sessionEstablished } = authSlice.actions;

export const {
  selectCurrentUser,
  selectAuthStatus,
  selectIsAuthenticated,
  selectIsSessionResolved,
} = authSlice.selectors;

export const authReducer = authSlice.reducer;

/**
 * Role check for hiding UI a user cannot act on.
 *
 * This is presentation only. Hiding a button does not prevent the request it
 * would have sent; the backend must reject unauthorized calls regardless.
 */
export function hasRole(user: User | null, allowed: readonly UserRole[]): boolean {
  return user !== null && allowed.includes(user.role);
}
